import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ShieldCheck, Activity } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = [
  { text: "Your Gateway to", cls: "text-slate-900" },
  { text: "Unlisted & Pre-IPO", cls: "gold-text" },
  { text: "Investment Opportunities", cls: "text-slate-900" },
];

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

export const Hero = ({ opportunities = [] }) => {
  const featured = opportunities.find((o) => o.slug === "india-gold-metaverse") || opportunities[0];
  const STATS = [
    { value: "₹1.2T+", label: "Private Capital Tracked" },
    { value: `${opportunities.length || 48}+`, label: "Active Pre-IPO Names" },
    { value: "Live", label: "Liquidity Index" },
  ];
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 700], [0, 140]);
  const yCards = useTransform(scrollY, [0, 700], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" onMouseMove={onMouseMove} className="hero-spotlight relative flex min-h-screen items-center overflow-hidden pt-[72px]">
      <motion.div style={{ y: yImage }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1565515636369-57f6e9f5fe79?fm=jpg&q=80&w=2400&auto=format&fit=crop"
          alt=""
          className="h-[120%] w-full object-cover opacity-[0.55]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/50 via-[#FFFFFF]/30 to-[#FFFFFF]" />
      </motion.div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 py-24 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-mono2 mb-8 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90"
          >
            Private Markets · Unlisted Securities · Est. Desk
          </motion.p>

          <h1 data-testid="hero-title" className="font-display text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-[4.4rem]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg"
          >
            Market insights, research and professional guidance for the unlisted share market — transparent information supporting investors through every step of the journey.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-opportunities-button"
              onClick={() => scrollToId("#opportunities")}
              className="group flex items-center gap-3 bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-transform duration-300 hover:scale-[1.03]"
            >
              Explore Opportunities
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button
              data-testid="hero-talk-to-expert-button"
              onClick={() => scrollToId("#enquire")}
              className="border border-slate-300 px-8 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-slate-800 transition-colors duration-300 hover:border-[#6D28D9]/50 hover:text-[#6D28D9]"
            >
              Talk to an Expert
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-16 grid max-w-xl grid-cols-1 divide-y divide-slate-200 border-y border-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
          >
            {STATS.map((s) => (
              <div key={s.label} className="px-5 py-4 sm:py-5 sm:first:pl-0">
                <p className="font-display whitespace-nowrap text-2xl font-semibold text-[#6D28D9] sm:text-3xl">{s.value}</p>
                <p className="font-mono2 mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yCards, rotateX: rx, rotateY: ry, transformPerspective: 900 }} className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="relative border border-[#6D28D9]/20 bg-[#F6F4FD]/70 p-8 backdrop-blur-xl"
          >
            <div className="absolute -left-px -top-px h-8 w-8 border-l-2 border-t-2 border-[#6D28D9]" />
            <div className="absolute -bottom-px -right-px h-8 w-8 border-b-2 border-r-2 border-[#6D28D9]" />
            <div className="flex items-center justify-between">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">Desk Snapshot</p>
              <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-emerald-600">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" /> Live
              </span>
            </div>
            <p data-testid="hero-featured-name" className="font-display mt-6 text-lg font-medium text-slate-800">{featured?.name || "Loading board…"}</p>
            <div className="mt-2 flex items-end gap-3">
              <p data-testid="hero-featured-price" className="font-display text-5xl font-semibold text-slate-900">{featured ? `₹${fmt.format(featured.price)}` : "—"}</p>
              {typeof featured?.change_pct === "number" && <p className={`font-mono2 pb-1.5 text-sm ${featured.change_pct < 0 ? "text-rose-600" : "text-emerald-600"}`}>{featured.change_pct >= 0 ? "+" : ""}{featured.change_pct}% 1Y</p>}
            </div>
            <div className="mt-8 space-y-3 border-t border-slate-200 pt-6">
              {[
                { icon: ShieldCheck, text: "SEBI-compliant assisted channels" },
                { icon: Activity, text: "Indicative pricing refreshed weekly" },
              ].map((f, i) => (
                <div key={f.text} className="flex items-center gap-3 text-sm text-slate-500">
                  <f.icon className="h-4 w-4 text-[#6D28D9]" /> {f.text}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
